import type { Config } from 'prebid.js'

import type { ActiveCmps, HeaderBiddingOptions } from './types'

export const BIDDER_TIMEOUT = 1500

const GDPR_TIMEOUT = 8000
const GDPR_ACTION_TIMEOUT = 10000

const ID5_PARTNER_ID = 1920

export const getPrebidConfig = (options: HeaderBiddingOptions, activeCmps: ActiveCmps): Partial<Config> => {
    const id5PDParams: Record<string, string> = {}
    if (options.hashedEmail) {
        // https://wiki.id5.io/docs/passing-partner-data-to-id5#normalizing-emails-prior-to-hashing
        id5PDParams[1] = options.hashedEmail
    }
    const id5PDString = new URLSearchParams(id5PDParams).toString()

    // TODO waiting for creds
    // const connectIdHEString = await sha256hex(user.email).toLowerCase()

    const config: Partial<Config> = {
        priceGranularity: 'custom',
        customPriceBucket: {
            buckets: [
                {
                    max: 2.0,
                    increment: 0.01,
                },
                {
                    max: 10.0,
                    increment: 0.05,
                },
                {
                    max: 30.0,
                    increment: 0.5,
                },
                {
                    max: 250.0,
                    increment: 1.0,
                },
            ],
        },
        debug: options.debug,
        bidderTimeout: BIDDER_TIMEOUT,
        // we need cache to get valid url link for imasdk
        cache: {
            url: 'https://ssp.playgama.com/v1/cache',
            // Onetag has own cached vast url, but to use it we need do massive changes on GAM side,
            // so this is a legit workaround for such cases
            ignoreBidderCacheKey: true,
        },
        // TODO add default rates for fallback values
        currency: {
            adServerCurrency: 'USD',
        },
        // show only winner bid to GPT
        enableSendAllBids: false,
        consentManagement: {
            strictStorageEnforcement: true, // todo do we need?
        },
        userSync: {
            filterSettings: {
                iframe: {
                    bidders: '*',
                    filter: 'include',
                },
            },
            userIds: [
                {
                    name: 'id5Id',
                    params: {
                        partner: ID5_PARTNER_ID,
                        externalModuleUrl: 'https://cdn.id5-sync.com/api/1.0/id5PrebidModule.js',
                        pd: id5PDString,
                        canCookieSync: true, // optional, has effect only when externalModuleUrl is used
                        gamTargetingPrefix: 'id5', // optional, when set the ID5 module will set gam targeting paramaters with this prefix
                        exposeTargeting: true, // optional, when set the ID5 module will execute `window.id5tags.cmd` callbacks for custom targeting tags
                    },
                    storage: {
                        type: 'html5', // "html5" is the recommended storage type
                        name: 'id5id', // "id5id" is the recommended storage name
                        expires: 90, // storage lasts for 90 days
                        refreshInSeconds: 7200, // refresh ID every 2 hours to ensure it's fresh
                    },
                },
                // waiting for pixelId
                // {
                //     name: 'connectId',
                //     params: {
                //         pixelId: '0000',
                //         he: connectIdHEString,
                //         puid: this.config.pgAuid,
                //     },
                // },
                {
                    name: 'sharedId',
                    storage: {
                        type: 'html5',
                        name: '_pubcid',
                        expires: 365,
                    },
                },
            ],
            encryptedSignalSources: {
                sources: [
                    {
                        source: ['id5-sync.com', 'pubcid.org'],
                        encrypt: false,
                        customFunc: () => {
                            throw new Error('Should not be called')
                        },
                    },
                ],
                registerDelay: 0,
            },
        },
        enableTIDs: true,
        consistentTIDs: true,
    }

    if (process.env.NODE_ENV !== 'production' || (!activeCmps.gpp && !activeCmps.tcf)) {
        delete config.consentManagement
    }

    if (config.consentManagement && activeCmps.tcf) {
        config.consentManagement.gdpr = {
            cmpApi: 'iab',
            timeout: GDPR_TIMEOUT,
            actionTimeout: GDPR_ACTION_TIMEOUT,
            defaultGdprScope: true,
            rules: [
                {
                    purpose: 'storage',
                    enforcePurpose: true,
                    enforceVendor: true,
                    vendorExceptions: ['playgama'],
                },
                {
                    purpose: 'basicAds',
                    enforcePurpose: true,
                    enforceVendor: true,
                    vendorExceptions: ['playgama'],
                },
                {
                    purpose: 'measurement',
                    enforcePurpose: true,
                    enforceVendor: true,
                    vendorExceptions: ['playgama'],
                },
                {
                    purpose: 'personalizedAds',
                    enforcePurpose: true,
                    enforceVendor: true,
                    vendorExceptions: ['playgama'],
                },
            ],
        }
    }

    if (config.consentManagement && activeCmps.gpp) {
        config.consentManagement.gpp = {
            cmpApi: 'iab',
            timeout: GDPR_TIMEOUT,
            actionTimeout: GDPR_ACTION_TIMEOUT,
        }
    }

    return config
}
